
import { useState, useEffect } from 'react';

export default function Home() {
  const [activeSection, setActiveSection] = useState('');
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    {
      id: 'infrastructure',
      title: 'Infrastructure Solutions',
      subtitle: 'Enterprise-Grade Technology Infrastructure',
      description: 'Comprehensive infrastructure solutions designed to scale with your business needs, ensuring maximum performance, reliability, and security.',
      icon: 'ri-server-line',
      bgImage: 'https://readdy.ai/api/search-image?query=modern%20data%20center%20with%20servers%20racks%20and%20blue%20lighting%2C%20professional%20infrastructure%20technology%20background%2C%20high-tech%20server%20room%20with%20organized%20cable%20management%20and%20glowing%20LED%20indicators%2C%20clean%20minimal%20industrial%20design&width=1920&height=1080&seq=infra001&orientation=landscape',
      features: [
        'Scalable Architecture Design',
        '24/7 Monitoring & Support',
        'Disaster Recovery Planning',
        'Security Compliance'
      ]
    },
    {
      id: 'cloud',
      title: 'Cloud Computing',
      subtitle: 'AWS, Azure, Google Cloud',
      description: 'Multi-cloud expertise across all major platforms, enabling seamless migration, optimization, and management of your cloud infrastructure.',
      icon: 'ri-cloud-line',
      bgImage: 'https://readdy.ai/api/search-image?query=futuristic%20cloud%20computing%20concept%20with%20floating%20servers%20and%20data%20streams%2C%20digital%20transformation%20technology%20background%20with%20blue%20and%20white%20colors%2C%20modern%20cloud%20infrastructure%20visualization%20with%20geometric%20patterns&width=1920&height=1080&seq=cloud001&orientation=landscape',
      features: [
        'Multi-Cloud Strategy',
        'Cost Optimization',
        'Auto-Scaling Solutions',
        'DevOps Integration'
      ]
    },
    {
      id: 'datacenter',
      title: 'Data Center Management',
      subtitle: 'Complete Facility Operations',
      description: 'End-to-end data center management services including facility monitoring, capacity planning, and operational excellence.',
      icon: 'ri-building-line',
      bgImage: 'https://readdy.ai/api/search-image?query=state%20of%20the%20art%20data%20center%20facility%20with%20rows%20of%20server%20cabinets%2C%20professional%20lighting%20and%20cooling%20systems%2C%20modern%20enterprise%20data%20center%20with%20organized%20infrastructure%20and%20monitoring%20displays&width=1920&height=1080&seq=datacenter001&orientation=landscape',
      features: [
        'Facility Monitoring',
        'Capacity Planning',
        'Energy Efficiency',
        'Compliance Management'
      ]
    },
    {
      id: 'virtualization',
      title: 'Virtualization',
      subtitle: 'VMware, Hyper-V',
      description: 'Advanced virtualization solutions to maximize resource utilization, reduce costs, and improve operational efficiency across your infrastructure.',
      icon: 'ri-stack-line',
      bgImage: 'https://readdy.ai/api/search-image?query=abstract%20virtualization%20technology%20concept%20with%20layered%20virtual%20machines%20and%20hypervisor%20architecture%2C%20digital%20infrastructure%20with%20interconnected%20virtual%20environments%20and%20transparent%20layers&width=1920&height=1080&seq=virtual001&orientation=landscape',
      features: [
        'Resource Optimization',
        'Virtual Machine Management',
        'High Availability',
        'Performance Monitoring'
      ]
    },
    {
      id: 'server',
      title: 'Server Installation',
      subtitle: 'Hardware & Management',
      description: 'Professional server installation, configuration, and ongoing management services to ensure optimal performance and reliability.',
      icon: 'ri-computer-line',
      bgImage: 'https://readdy.ai/api/search-image?query=professional%20server%20installation%20and%20maintenance%20with%20technician%20working%20on%20enterprise%20hardware%2C%20modern%20server%20room%20environment%20with%20organized%20cable%20management%20and%20professional%20equipment%20setup&width=1920&height=1080&seq=server001&orientation=landscape',
      features: [
        'Hardware Installation',
        'Configuration Management',
        'Performance Tuning',
        'Maintenance Services'
      ]
    },
    {
      id: 'network',
      title: 'Network Design',
      subtitle: 'Implementation & Optimization',
      description: 'Comprehensive network design and implementation services to create robust, secure, and high-performance network infrastructures.',
      icon: 'ri-router-line',
      bgImage: 'https://readdy.ai/api/search-image?query=advanced%20network%20infrastructure%20design%20with%20fiber%20optic%20cables%20and%20network%20switches%2C%20modern%20data%20center%20networking%20equipment%20with%20glowing%20connections%20and%20organized%20cable%20management%2C%20professional%20network%20topology%20visualization&width=1920&height=1080&seq=network001&orientation=landscape',
      features: [
        'Network Architecture',
        'Security Implementation',
        'Performance Optimization',
        'Redundancy Planning'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed transition-transform duration-1000"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=panoramic%20view%20of%20modern%20technology%20infrastructure%20with%20servers%2C%20network%20equipment%20and%20data%20centers%2C%20futuristic%20digital%20transformation%20landscape%20with%20blue%20and%20purple%20lighting%2C%20enterprise%20technology%20ecosystem%20visualization&width=1920&height=1080&seq=hero001&orientation=landscape')`,
            transform: `translateY(${scrollY * 0.5}px)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
        
        <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="transform transition-all duration-1000 hover:scale-105">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent animate-pulse">
              Infrastructure Solutions
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl mb-12 text-gray-200 max-w-4xl mx-auto leading-relaxed">
              Enterprise-grade technology infrastructure designed for the future
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300" />
                <button className="relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-700 text-white rounded-full font-semibold hover:from-blue-700 hover:to-purple-800 transform hover:scale-110 transition-all duration-300 shadow-2xl">
                  Explore Solutions
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl" />
        </div>
      </section>

      {/* Solutions Sections */}
      {sections.map((section, index) => (
        <section 
          key={section.id}
          className="relative min-h-screen flex items-center overflow-hidden"
          onMouseEnter={() => setActiveSection(section.id)}
          onMouseLeave={() => setActiveSection('')}
        >
          {/* Background Image with Parallax */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-fixed transition-all duration-700"
            style={{
              backgroundImage: `url('${section.bgImage}')`,
              transform: `scale(${activeSection === section.id ? 1.1 : 1})`,
              filter: `brightness(${activeSection === section.id ? 0.7 : 0.5})`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
          
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center transition-all duration-700 ${
              index % 2 === 0 ? '' : 'lg:grid-cols-2 lg:gap-16'
            }`}>
              {/* Content */}
              <div className={`text-white space-y-6 sm:space-y-8 transform transition-all duration-700 hover:translate-x-4 ${
                index % 2 === 1 ? 'lg:order-2' : ''
              }`}>
                <div className="flex items-center space-x-4 mb-4">
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 transform transition-all duration-500 hover:rotate-12 hover:scale-110 ${
                    activeSection === section.id ? 'scale-110 rotate-6' : ''
                  }`}>
                    <i className={`${section.icon} text-2xl sm:text-3xl text-white`} />
                  </div>
                  <div>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                      {section.title}
                    </h2>
                    <p className="text-lg sm:text-xl text-blue-300">{section.subtitle}</p>
                  </div>
                </div>
                
                <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl">
                  {section.description}
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {section.features.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex}
                      className="group flex items-center space-x-3 p-4 rounded-lg bg-black/30 backdrop-blur-sm border border-white/10 hover:border-blue-400/50 transition-all duration-300 hover:bg-black/50 transform hover:scale-105"
                    >
                      <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full group-hover:scale-150 transition-transform duration-300" />
                      <span className="text-sm sm:text-base text-gray-200 group-hover:text-white transition-colors duration-300">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 3D Interactive Element */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="relative group cursor-pointer">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 transform group-hover:scale-110" />
                  
                  <div className={`relative bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-white/10 transform transition-all duration-700 hover:scale-105 hover:rotate-1 ${
                    activeSection === section.id ? 'scale-105 rotate-1 shadow-2xl shadow-blue-500/20' : ''
                  }`}>
                    {/* 3D Card Content */}
                    <div className="space-y-6">
                      <div className="flex justify-center">
                        <div className={`w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 transform transition-all duration-700 group-hover:rotate-12 ${
                          activeSection === section.id ? 'rotate-12 scale-110' : ''
                        }`}>
                          <i className={`${section.icon} text-3xl sm:text-4xl text-white`} />
                        </div>
                      </div>
                      
                      <div className="text-center space-y-4">
                        <h3 className="text-xl sm:text-2xl font-bold text-white">
                          {section.title}
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          {section.features.slice(0, 4).map((feature, idx) => (
                            <div key={idx} className="p-3 rounded-lg bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all duration-300 transform hover:scale-105">
                              <p className="text-xs sm:text-sm text-gray-300 text-center">
                                {feature}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Final CTA Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=futuristic%20technology%20landscape%20with%20interconnected%20infrastructure%20elements%2C%20modern%20enterprise%20data%20center%20with%20glowing%20connections%20and%20network%20visualization%2C%20professional%20technology%20ecosystem%20background&width=1920&height=1080&seq=cta001&orientation=landscape')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
        
        <div className="relative z-10 text-center text-white max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent">
            Ready to Transform Your Infrastructure?
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl mb-12 text-gray-200 leading-relaxed">
            Let's build the foundation for your digital future together
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300" />
              <button className="relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-700 text-white rounded-full font-semibold hover:from-blue-700 hover:to-purple-800 transform hover:scale-110 transition-all duration-300 shadow-2xl whitespace-nowrap">
                Get Started Today
              </button>
            </div>
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-cyan-600 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300" />
              <button className="relative px-8 py-4 bg-transparent border-2 border-purple-500 text-white rounded-full font-semibold hover:bg-purple-500/20 transform hover:scale-110 transition-all duration-300 shadow-2xl whitespace-nowrap">
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
